import { tz } from "@date-fns/tz";
import {
  formatDraftReservationDate,
  formatDurationToDays,
  formatReservationDate,
  useServiceCalendar,
  useToday,
  useV2GetCheckoutQuery,
} from "@stepapp/core";
import { spacing, Typography } from "@stepapp/ui";
import { addDays, addWeeks, endOfWeek, format, isBefore, isSameWeek, startOfWeek } from "date-fns";
import { useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet } from "react-native";
import {
  ServiceNotice,
  ServiceSceneDescription,
  ServiceWeekCalendar,
} from "../../../src/components";
import { useAppDispatch, useAppSelector } from "../../../src/hooks";
import { setEndDate, setStartDate } from "../../../src/store/calendar";
import type { ReservationFormParams } from "../../../src/store/draftReservation/types";

const DateScreen = () => {
  const { t } = useTranslation();
  const today = useToday();
  const appDispatch = useAppDispatch();
  const { slug = "" } = useLocalSearchParams<{ slug: string }>();
  const { setValue, watch } = useFormContext<ReservationFormParams>();
  const [timeConsumption, startDate, locationId] = watch([
    "meta.timeConsumption",
    "startDate",
    "locationId",
  ]);
  const configTimezone = useAppSelector(({ appConfig }) => appConfig.timezone);

  const [canChangeWeek, setCanChangeWeek] = useState(true);

  // biome-ignore lint/correctness/useExhaustiveDependencies: not required
  const initialDate = useMemo(
    () => startDate && formatReservationDate(startDate, "yyyy-MM-dd'T'HH:mm:ssXXX"),
    [],
  );

  const calendarStartDate = useAppSelector(({ calendar }) => calendar.startDate);
  const calendarEndDate = useAppSelector(({ calendar }) => calendar.endDate);

  const {
    selectedDate,
    handleDayPress,
    handleHourPress,
    hours,
    markedDates,
    selectedHour,
    loading,
    resetDateAndHour,
  } = useServiceCalendar({
    bundleSlug: slug,
    locationId,
    date: initialDate || "",
    timeConsumption,
    calendarStartDate,
    calendarEndDate,
  });

  const { data } = useV2GetCheckoutQuery();

  const isOneTime = !formatDurationToDays(data?.v2GetCheckout.periodicity?.cycleDuration || "P0D");

  // biome-ignore lint/correctness/useExhaustiveDependencies: not required
  useEffect(() => {
    if (selectedHour && selectedDate) {
      const newDate = formatDraftReservationDate(`${selectedDate} ${selectedHour}`);
      setValue("startDate", newDate, { shouldValidate: true });
      return;
    }
    setValue("startDate", null);
  }, [selectedDate, selectedHour]);

  const handleStartDay = useCallback(() => {
    const tomorrow = addDays(today, 1).toString();

    if (initialDate) {
      const isPastDay = isBefore(initialDate, tomorrow);
      return isPastDay ? tomorrow : startOfWeek(initialDate).toString();
    }
    return tomorrow;
  }, [today, initialDate]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    const selectedStartDay = handleStartDay();
    const lastWeekDay = endOfWeek(selectedStartDay).toString();

    appDispatch(setStartDate(format(selectedStartDay, "yyyy-MM-dd")));
    appDispatch(setEndDate(format(lastWeekDay, "yyyy-MM-dd")));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    if (loading) return;

    const isFirstWeekFetch =
      isSameWeek(today, calendarStartDate, { in: tz(configTimezone) }) &&
      Object.keys(markedDates).length === 0;

    if (isFirstWeekFetch && canChangeWeek) {
      setCanChangeWeek(false);
      const nextWeekStartDate = startOfWeek(addWeeks(today, 1)).toISOString();

      const nextWeekEndDate = endOfWeek(addWeeks(today, 1)).toISOString();

      appDispatch(setStartDate(format(nextWeekStartDate, "yyyy-MM-dd")));
      appDispatch(setEndDate(format(nextWeekEndDate, "yyyy-MM-dd")));
    }
  }, [loading]);

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Typography variant="newH2">{t("pages.serviceDateAndAddress.title")}</Typography>

      <ServiceSceneDescription description={t("pages.serviceDateAndAddress.description")} />

      <ServiceWeekCalendar
        hours={hours}
        startDate={startDate || ""}
        handleDayPress={handleDayPress}
        handleHourPress={handleHourPress}
        resetSelectedDate={resetDateAndHour}
        markedDates={markedDates}
        selectedDate={selectedDate}
        selectedHour={selectedHour}
        loading={loading}
        isReservationFlow={true}
        containerStyles={styles.calendar}
      />

      <ServiceNotice type={isOneTime ? "oneTime" : "subscription"} />

      <ServiceNotice type="date" />
    </ScrollView>
  );
};

export default DateScreen;

const styles = StyleSheet.create({
  calendar: {
    paddingBottom: spacing.unit * 2,
  },
  container: {
    marginBottom: spacing.unit * 2,
    paddingTop: spacing.unit * 2,
  },
});
